import {ComplianceManagerApi} from "../../../compliance-manager";
import {Button, Col, Descriptions, Icon, List, Row, Tooltip, Typography} from "antd";
import ComplianceStatusButton from "components/compliance/compliance-status-button";
import './overall-tab.style.less';
import {momentOrNull} from "common/util";
import useBreakpoints from "hooks/useBreakpoints";
import {
    BrdRestrictionStatus,
    ComplianceEngineResult,
    ComplianceProviderResultFragmentFragment
} from "__generated__/graphql/types";
import {usePaxNodeQuery} from "../../../../graphql";
import React from "react";
import {mapProviderTypeToTab} from "../../../state";
import moment from "moment";
import {scanForProviders} from "../../../../providers/provider-scanner";
import {OverrideButton} from "../../override-button";
import {OverrideCard} from "../../../../../isn/OverrideCard";
import {NextExpirationDate} from "../../../../NextExpirationDate";

/**
 * Tab for displaying unified compliance result.
 */
export default function OverallTab(props: { api: ComplianceManagerApi }){

    const { api } = props;

    const bp = useBreakpoints();

    const paxID = api.paxContext?.paxNode?._id;

    const { paxNode } = usePaxNodeQuery(paxID);

    const isSmallScreen = bp.max("medium");
    const complianceResult = api.complianceResult;

    const brdRestriction = api.paxContext?.paxNode?.authorization?.brdRestriction_Compliance;
    const restrStatus = brdRestriction?.status;

    if (!complianceResult){
        return <div style={{ marginTop: 12 }}>
            <Typography.Text type="secondary">No Data</Typography.Text>
        </div>
    }

    const providerResults = scanForProviders(complianceResult);

    // const failingDetails: VeriflytResultFragmentFragment['failingDetails'] = complianceResult.failingDetails || [];
    // const passingDetails: VeriflytResultFragmentFragment['passingDetails'] = complianceResult.passingDetails || [];
    // const hasFailures = failingDetails.length > 0;
    // const hasPasses = passingDetails.length > 0;
        const hasError = complianceResult.error && complianceResult.error.length > 0;

    function renderCause(){
        const causeProvider = complianceResult?.causedByProvider;
        const providerList = scanForProviders(complianceResult);
        const causeResult = providerList.find((p) => p.providerType === causeProvider);

        if (!causeResult) return null;

        return <Descriptions
            title="Caused By Provider"
            bordered
            style={{ marginTop: 12 }}
            size="small"
            column={1}
        >
            <Descriptions.Item label="Provider">
                <Row type="flex" justify="space-between">
                    <Col>
                        {causeResult.providerType}
                    </Col>
                    <Col>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => api.setTab(mapProviderTypeToTab(causeProvider))}
                        >View</Button>
                    </Col>
                </Row>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
                <ComplianceStatusButton
                    pax={api.paxContext}
                    selectedProvider={causeProvider}
                    disabled
                    showSourcesTooltip={false}
                />
            </Descriptions.Item>
            <Descriptions.Item label="Reason">
                {causeResult.reason || 'N/A'}
            </Descriptions.Item>
        </Descriptions>
    }

    function renderPolicyLog(){

        if (!complianceResult?.policyLog) return null;

        function renderLog(log: ComplianceEngineResult['policyLog'][0], index: number){
            return (
                <List.Item key={index}>
                    <Typography.Text>({index + 1}) {log.message}</Typography.Text>
                </List.Item>
            )
        }

        const icon = <Tooltip title="Log of policy decisions taken during compliance evaluation.">
            <Icon type="question-circle" />
        </Tooltip>

        return <div style={{ marginTop: 12 }}>
            <div className="ant-descriptions-title">Policy Log {icon}</div>
            <List
                bordered
                style={{ marginTop: 12 }}
                size="small"
            >
                {complianceResult.policyLog.map(renderLog)}
            </List>
        </div>
    }

    function renderOverrideCard(){
        if (restrStatus == BrdRestrictionStatus.OVERRIDDEN) {
            return <OverrideCard
                data={brdRestriction}
                paxData={paxNode}
                size="small"
            />
        }
        return null;
    }

    function renderProviderResult(result: ComplianceProviderResultFragmentFragment){
        return (
            <Descriptions.Item label={
                <Row type="flex" justify="space-between" align="middle">
                    <Col>
                        {result.providerType}
                    </Col>
                    {bp.min('medium') ? (
                        <Col>
                            {result.latencyMs} ms
                        </Col>
                    ) : null}

                </Row>
            }>
                <Row type="flex" justify="space-between">
                    <Col>
                        <ComplianceStatusButton
                            pax={api.paxContext}
                            selectedProvider={result.providerType}
                            disabled
                            showSourcesTooltip={false}
                        />
                    </Col>
                    <Col>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => api.setTab(mapProviderTypeToTab(result.providerType))}
                        >View</Button>
                    </Col>
                </Row>
            </Descriptions.Item>
        )
    }

    function renderProviderResults(){
        if (!providerResults.length) return null;

        return (
            <Descriptions
                title="Provider Results"
                bordered
                style={{ marginTop: 12 }}
                size="small"
                column={1}
            >
                {providerResults.map(renderProviderResult)}
            </Descriptions>
        )
    }

    return <div style={{ marginTop: 12 }}>
        <Row style={{marginTop: "1.5rem", marginBottom: "1rem"}} type="flex" gutter={12}>
            {paxNode ? (
                <Col>
                    <h4
                        style={{ margin: 0 }}
                    >Overall Details for <span style={{textTransform: 'capitalize'}}>{paxNode?.lastName}</span>, <span style={{textTransform: 'capitalize'}} >{paxNode?.firstName}</span></h4>
                </Col>
            ) : null}

            {restrStatus == BrdRestrictionStatus.NEEDS_OVERRIDE ? (
                <Col style={{ marginLeft: 'auto' }}>
                    <OverrideButton api={api} style={{ position: 'relative', top: bp.max('medium') ? 0 : '-0.5em' }} />
                </Col>
            ) : null}
        </Row>
        {renderOverrideCard()}
        <Descriptions
            className="mc-compliance-manager-details"
            bordered
            column={1}
            size="small"
            layout={isSmallScreen ? 'vertical' : 'horizontal'}
        >
            <Descriptions.Item label="Overall Status">
                <ComplianceStatusButton
                    pax={api.paxContext}
                    disabled
                    showSourcesTooltip={false}
                />
            </Descriptions.Item>
            <Descriptions.Item label="Last Check Time">
                {momentOrNull(complianceResult.startTs)?.format('YYYY-MM-DD HH:MM')}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
                {moment.duration(complianceResult.totalLatencyMs)?.asMilliseconds()} ms
            </Descriptions.Item>
            {hasError ? (
                <Descriptions.Item label="Error Message" className="mc-compliance-manager-error">
                    <Typography.Text type="danger">{complianceResult.error}</Typography.Text>
                </Descriptions.Item>
            ) : null}
            {!!complianceResult.nextExpirationDate && (
                <Descriptions.Item label="Next Expiration">
                    <NextExpirationDate date={complianceResult.nextExpirationDate?.nextExpirationDate} />
                    {' '}
                    {complianceResult.nextExpirationDate?.nextExpirationQualificationName ? `(${complianceResult.nextExpirationDate?.nextExpirationQualificationName})` : ''}
                </Descriptions.Item>
            )}
            {complianceResult.checkInPackage?.fsCustomerDoc ? (
                <Descriptions.Item label="Customer">
                    {complianceResult.checkInPackage.fsCustomerDoc?.name}
                </Descriptions.Item>
            ) : null}
            {complianceResult.checkInPackage?.fsEmployerDoc ? (
                <Descriptions.Item label="Employer">
                    {complianceResult.checkInPackage.fsEmployerDoc?.name}
                </Descriptions.Item>
            ) : null}
            {complianceResult.checkInPackage?.fsContractorDoc ? (
                <Descriptions.Item label="Employer">
                    {complianceResult.checkInPackage.fsContractorDoc?.name}
                </Descriptions.Item>
            ) : null}
            {complianceResult.checkInPackage?.fsDepartureDoc ? (
                <Descriptions.Item label="Departure">
                    {complianceResult.checkInPackage.fsDepartureDoc?.name}
                </Descriptions.Item>
            ) : null}
            {complianceResult.checkInPackage?.fsDestinationDoc ? (
                <Descriptions.Item label="Destination">
                    {complianceResult.checkInPackage.fsDestinationDoc?.name}
                </Descriptions.Item>
            ) : null}
        </Descriptions>
        {renderCause()}
        {renderPolicyLog()}
        {renderProviderResults()}
    </div>
}